import {useI18n} from '@/i18n'
import {Card, CardContent, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {MutedText} from '@/components/shared/MutedText'
import {Button} from '@/components/ui/button'
import {useDialog} from '@/hooks/useDialog'
import type {OutboundConnector} from '@/types/outbound-connector'
import {useSapAbapConfig, useCreateSapAbapConfig} from '@/hooks/use-sap-configs'
import {SapAbapOutboundConfigCard} from '../sap-abap/SapAbapOutboundConfigCard'
import {SapAbapOutboundConfigEditDialog} from '../sap-abap/SapAbapOutboundConfigEditDialog'

interface SapAbapConfigCardProps {
    connector: OutboundConnector
    canUpdate: boolean
}

/**
 * Wrapper that bridges the outbound-detail page's `connector` prop convention
 * to {@link SapAbapOutboundConfigCard} and the corresponding edit dialog.
 * Handles loading + empty state and threads the per-row "Edit" button through
 * to the dialog when the caller is allowed to manage outbound connectors.
 */
export function SapAbapConfigCard({connector, canUpdate}: SapAbapConfigCardProps) {
    const i18n = useI18n()
    const {data: config, isLoading, error} = useSapAbapConfig(connector.id)
    const editDialog = useDialog()
    const createMutation = useCreateSapAbapConfig(connector.id)

    if (isLoading) {
        return (
            <Card>
                <CardContent>
                    <MutedText>{i18n.common.loading}</MutedText>
                </CardContent>
            </Card>
        )
    }
    if (error || !config) {
        return (
            <Card>
                <RowCardHeader>
                    <CardTitle data-testid="_section">{i18n.connectors.sapAbap.connectionSection}</CardTitle>
                    {canUpdate && !error && (
                        <Button
                            data-testid="btn-create-sap-abap-config"
                            size="sm"
                            onClick={() => createMutation.mutate({})}
                            disabled={createMutation.isPending}
                        >
                            {i18n.common.create}
                        </Button>
                    )}
                </RowCardHeader>
                <CardContent>
                    <MutedText>{i18n.connectors.sapAbap.connectionDescription}</MutedText>
                </CardContent>
            </Card>
        )
    }
    return (
        <>
            <SapAbapOutboundConfigCard config={config} canUpdate={canUpdate} onEdit={editDialog.open}/>
            <SapAbapOutboundConfigEditDialog
                connectorId={connector.id}
                config={config}
                open={editDialog.isOpen}
                onClose={editDialog.close}
            />
        </>
    )
}
